// ═══════════════════════════════════════════════════════════
// Staff — Public Records Library Management
// Upload, categorize, publish/unpublish general government
// documents for the public portal Records Library.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import {
  IconPlus, IconLoader2, IconFileText, IconTrash, IconUpload,
} from '@tabler/icons-react';
import { useAuthStore } from '../../../store/authStore';
import {
  listLibraryDocuments, createLibraryDocument, updateLibraryDocument,
  deleteLibraryDocument, type LibraryDocument,
} from '../../../api/library';

const CATEGORIES = [
  'Council & Board Meetings', 'Budgets & Finance', 'Policies & Ordinances',
  'Reports & Studies', 'Contracts & Procurement', 'Public Safety',
  'Planning & Zoning', 'Other',
];

function formatBytes(bytes: number) {
  if (!bytes) return '—';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function StaffLibrary() {
  const [docs, setDocs] = useState<LibraryDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [form, setForm] = useState({ title: '', description: '', category: 'Other' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const actorEmail = useAuthStore(s => s.user?.email) ?? '';

  useEffect(() => {
    listLibraryDocuments().then(setDocs).finally(() => setLoading(false));
  }, []);

  async function handleUpload() {
    if (!pendingFile || !form.title.trim()) return;
    setUploading(true);
    try {
      const doc = await createLibraryDocument({
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        file: pendingFile,
        uploadedBy: actorEmail,
      });
      setDocs(prev => [doc, ...prev]);
      setForm({ title: '', description: '', category: 'Other' });
      setPendingFile(null);
      setAdding(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleTogglePublish(doc: LibraryDocument) {
    const next = !doc.isPublished;
    await updateLibraryDocument(doc.recordId, { isPublished: next });
    setDocs(prev => prev.map(d => d.recordId === doc.recordId ? { ...d, isPublished: next } : d));
  }

  async function handleDelete(doc: LibraryDocument) {
    if (!confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    await deleteLibraryDocument(doc.recordId);
    setDocs(prev => prev.filter(d => d.recordId !== doc.recordId));
  }

  const published = docs.filter(d => d.isPublished).length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0 }}>Records Library</h1>
          <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: '4px 0 0' }}>
            Upload general government documents for the public to browse and download.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" style={{ gap: 4 }} onClick={() => setAdding(true)}>
          <IconPlus size={14} /> Add Document
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: 'var(--color-primary-50)', color: 'var(--color-primary-700)' }}>
          {docs.length} total
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: 'var(--cds-green-50)', color: 'var(--cds-green-700)' }}>
          {published} published
        </span>
        {docs.length > published && (
          <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: 'var(--color-neutral-100)', color: 'var(--color-neutral-500)' }}>
            {docs.length - published} draft
          </span>
        )}
      </div>

      {/* Upload form */}
      {adding && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Upload New Document</div>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.pptx,.png,.jpg,.jpeg,.zip"
            onChange={e => { setPendingFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label required">Title</label>
              <input type="text" className="form-input" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. City Council Meeting Minutes — May 2026" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label">Category</label>
                <select className="form-select" value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label required">File</label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: '2px dashed var(--color-neutral-300)', borderRadius: 4,
                    padding: '8px 16px', textAlign: 'center', cursor: 'pointer',
                    background: pendingFile ? 'var(--cds-green-50)' : 'var(--color-neutral-50)',
                    fontSize: 14, color: pendingFile ? 'var(--cds-green-700)' : 'var(--color-neutral-500)',
                  }}>
                  {pendingFile ? (
                    <span><IconUpload size={14} style={{ verticalAlign: -2 }} /> {pendingFile.name}</span>
                  ) : (
                    'Click to choose file…'
                  )}
                </div>
              </div>
            </div>

            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Description (optional)</label>
              <textarea className="form-textarea" rows={2} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of the document contents" />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setAdding(false); setPendingFile(null); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={uploading || !pendingFile || !form.title.trim()}
                onClick={handleUpload} style={{ gap: 4 }}>
                {uploading ? <><IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Uploading…</> : <><IconUpload size={12} /> Upload & Publish</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-400)' }}>
          <IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : docs.length === 0 && !adding ? (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <IconFileText size={32} style={{ color: 'var(--color-neutral-300)', marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-600)' }}>No library documents yet</div>
          <div style={{ fontSize: 14, color: 'var(--color-neutral-400)', marginTop: 4 }}>
            Upload council minutes, budgets, reports, and other public documents.
          </div>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 16, gap: 4 }} onClick={() => setAdding(true)}>
            <IconPlus size={14} /> Add First Document
          </button>
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 4, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Title</th>
                <th style={{ width: 160 }}>Category</th>
                <th style={{ width: 80 }}>Size</th>
                <th style={{ width: 100 }}>Uploaded</th>
                <th style={{ width: 90, textAlign: 'center' }}>Status</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.recordId}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{doc.title || doc.fileName}</div>
                    {doc.description && (
                      <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>
                        {doc.description}
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, background: 'var(--color-neutral-100)', color: 'var(--color-neutral-600)' }}>
                      {doc.category}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>{formatBytes(doc.fileSize)}</td>
                  <td style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{
                        fontSize: 12, padding: '2px 8px',
                        color: doc.isPublished ? 'var(--cds-green-700)' : 'var(--color-neutral-500)',
                        background: doc.isPublished ? 'var(--cds-green-50)' : 'var(--color-neutral-100)',
                        border: `1px solid ${doc.isPublished ? 'var(--cds-green-200)' : 'var(--color-neutral-200)'}`,
                      }}
                      onClick={() => handleTogglePublish(doc)}
                    >
                      {doc.isPublished ? '● Live' : '○ Draft'}
                    </button>
                  </td>
                  <td>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-neutral-400)', padding: 4 }}
                      onClick={() => handleDelete(doc)}
                      title="Delete"
                    >
                      <IconTrash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
